import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";

/**
 * Shared data-fetching hooks for the ad-hoc (non-dashboard) pages. These wrap
 * apiFetch with the same 401 handling useDashboardData established: on an
 * expired token we call logout() and nothing else — ProtectedRoute then
 * bounces to /login with the current location stashed as `from`, so signing
 * back in resumes the page the user was on (the deliberate-sign-out path in
 * Profile.jsx/Sidebar.jsx intentionally bypasses this by navigating first).
 */

/**
 * useApi(path, { enabled })
 *
 * Declarative GET. Fetches on mount and whenever `path` changes.
 *   data    : parsed JSON body (null until loaded / after a hard error)
 *   loading : true during the initial fetch and non-silent reloads
 *   error   : Error from apiFetch (never a 401 — that logs out instead)
 *   reload  : async ({ silent } = {}) => refetch. Awaitable, resolves with
 *             the fresh body. `silent: true` skips the loading flip so
 *             post-action refreshes don't blank the page back to a skeleton.
 *
 * `enabled: false` skips fetching entirely (loading stays false) — for
 * fetches that depend on a param that may not exist yet.
 */
export function useApi(path, { enabled = true } = {}) {
  const { logout } = useAuth();
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  // Monotonic sequence number: a fetch only commits its result if no newer
  // fetch (or unmount/path change) superseded it. Same job as the `cancelled`
  // flags in the effects this hook replaces.
  const seq = useRef(0);

  const reload = useCallback(async ({ silent = false } = {}) => {
    const id = ++seq.current;
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await apiFetch(path);
      if (id === seq.current) setState({ data, loading: false, error: null });
      return data;
    } catch (err) {
      if (id !== seq.current) return undefined;
      if (err.status === 401) {
        logout();
        return undefined;
      }
      // Silent reloads keep the stale data on screen next to the error;
      // initial loads have nothing worth keeping.
      setState((s) => ({ data: silent ? s.data : null, loading: false, error: err }));
      return undefined;
    }
  }, [path, logout]);

  useEffect(() => {
    if (!enabled) {
      seq.current += 1; // supersede any in-flight fetch
      setState({ data: null, loading: false, error: null });
      return undefined;
    }
    reload();
    return () => {
      seq.current += 1;
    };
  }, [enabled, reload]);

  return { ...state, reload };
}

/**
 * useApiAction()
 *
 * Imperative apiFetch (POST/PUT/DELETE, or one-off GETs) with the same 401
 * logout. Returns a stable `(path, options) => Promise<body>` — errors other
 * than 401 rethrow so callers keep their existing try/catch UI.
 */
export function useApiAction() {
  const { logout } = useAuth();
  return useCallback(async (path, options) => {
    try {
      return await apiFetch(path, options);
    } catch (err) {
      if (err.status === 401) logout();
      throw err;
    }
  }, [logout]);
}
